/**
 * Filter profile export/import (config recipe only).
 * Never includes profile id, admin ownership, or systemTemplate flags.
 */

import type { FilterProfileConfig, TradingFilterProfile } from '../services/cryptoPortfolioService';

export const FILTER_PROFILE_EXPORT_TYPE = 'TRADING_FILTER_PROFILE';
export const FILTER_PROFILE_EXPORT_SCHEMA_VERSION = 1;

export interface FilterProfileExportPayload {
  name: string;
  description?: string | null;
  config: FilterProfileConfig;
}

export interface FilterProfileExportFile {
  schemaVersion: number;
  type: string;
  exportedAt: string;
  profile: FilterProfileExportPayload;
}

export function buildFilterProfileExportPayload(profile: TradingFilterProfile): FilterProfileExportFile {
  return {
    schemaVersion: FILTER_PROFILE_EXPORT_SCHEMA_VERSION,
    type: FILTER_PROFILE_EXPORT_TYPE,
    exportedAt: new Date().toISOString(),
    profile: {
      name: (profile.name || 'Imported profile').trim(),
      description: profile.description ?? null,
      config: cloneConfig(profile.config),
    },
  };
}

export function downloadFilterProfileExport(profile: TradingFilterProfile): void {
  const payload = buildFilterProfileExportPayload(profile);
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safeName = (profile.name || `profile-${profile.id}`)
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || `profile-${profile.id}`;
  a.href = url;
  a.download = `filter-profile-${safeName}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function parseFilterProfileExportJson(raw: string): FilterProfileExportFile {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error('Invalid JSON file');
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Export file must be a JSON object');
  }
  const file = parsed as Partial<FilterProfileExportFile>;
  if (file.type !== FILTER_PROFILE_EXPORT_TYPE) {
    throw new Error('Not a filter profile export (unexpected type)');
  }
  if (file.schemaVersion == null || Number(file.schemaVersion) > FILTER_PROFILE_EXPORT_SCHEMA_VERSION) {
    throw new Error(`Unsupported schemaVersion: ${String(file.schemaVersion)}`);
  }
  if (!file.profile || typeof file.profile !== 'object') {
    throw new Error('Export file missing profile config');
  }
  const name = String(file.profile.name || '').trim();
  if (!name) {
    throw new Error('Profile name is required in the export file');
  }
  if (!file.profile.config || typeof file.profile.config !== 'object') {
    throw new Error('Export file missing profile.config');
  }
  return {
    schemaVersion: Number(file.schemaVersion) || 1,
    type: FILTER_PROFILE_EXPORT_TYPE,
    exportedAt: typeof file.exportedAt === 'string' ? file.exportedAt : new Date().toISOString(),
    profile: {
      name,
      description: file.profile.description != null ? String(file.profile.description) : null,
      config: cloneConfig(file.profile.config),
    },
  };
}

/** Create-payload for API; appends (imported) when the name already exists locally. */
export function buildFilterProfileImportCreatePayload(
  file: FilterProfileExportFile,
  existingNames: string[],
): { name: string; description: string | null; config: FilterProfileConfig } {
  const existing = new Set(existingNames.map(n => n.trim().toLowerCase()).filter(Boolean));
  let name = file.profile.name.trim();
  if (existing.has(name.toLowerCase())) {
    name = `${name} (imported)`;
  }
  let suffix = 2;
  while (existing.has(name.toLowerCase())) {
    name = `${file.profile.name.trim()} (imported ${suffix})`;
    suffix += 1;
  }
  return {
    name,
    description: file.profile.description != null ? String(file.profile.description).trim() || null : null,
    config: cloneConfig(file.profile.config),
  };
}

function cloneConfig(config?: FilterProfileConfig | null): FilterProfileConfig {
  return JSON.parse(JSON.stringify(config ?? { schemaVersion: 1 })) as FilterProfileConfig;
}
